<template>
    <div class="SLAInfoDocked q-pa-xs">
        <div class="row items-center q-mt-xs q-mb-sm">
            <div class="text-primary">
                {{ $t('visualization.SLAInfo.title') }}
            </div>
        </div>

        <div class="panel-content">
            <div class="q-pa-xs">
                <div class="row q-col-gutter-sm">
                    <div
                        v-for="(customSLAPct, index) in filteredPercentages"
                        :key="index"
                        class="col-12 col-sm-6 col-md-6 col-lg-3"
                    >
                        <q-card
                            flat
                            class="q-pa-xs SLACard"
                            :class="{
                                SLAIsCases: Array.isArray(customSLA) && customSLA.length > 1 && customSLAPct.variantId === 'all',
                            }"
                        >
                            <div class="row items-center q-col-gutter-sm SLACardRow">
                                <div class="col-auto">
                                    <template v-if="customSLAPct.variantId !== 'all'">
                                        <img
                                            height="24"
                                            width="24"
                                            alt=""
                                            :src="require(`@/assets/images/sla/${getIcon(customSLAPct)}.svg`)"
                                        >
                                        <QTooltip class="bg-gray-bg text-primary">
                                            {{ getTargetTypeLabel(customSLAPct) }}
                                        </QTooltip>
                                    </template>
                                    <template v-else>
                                        <QIcon name="fact_check" color="primary" size="24px" />
                                        <QTooltip class="bg-gray-bg text-primary">
                                            {{ getTargetTypeLabel(customSLAPct) }}
                                        </QTooltip>
                                    </template>
                                </div>

                                <div class="col SLAInfoCell">
                                    <div class="column">
                                        <div class="row items-center no-wrap">
                                            <div
                                                class="text-style ellipsis"
                                                :class="{ 'text-weight-bold': customSLAPct.variantId === 'all' }"
                                            >
                                                <QTooltip v-if="customSLAPct.variantId !== 'all' && getLabel(customSLAPct).length > 32" class="bg-gray-bg text-primary">
                                                    {{ getLabel(customSLAPct) }}
                                                </QTooltip>
                                                <span v-if="customSLAPct.variantId !== 'all'">{{ getLabel(customSLAPct) }}</span>
                                                <span v-else>{{ $t('visualization.SLAInfo.cases') }}: {{ cases }}</span>
                                            </div>
                                        </div>
                                        <div class="row items-center no-wrap">
                                            <div class="text-caption text-grey-7 ellipsis">
                                                {{ getTargetTypeLabel(customSLAPct) }}
                                            </div>
                                            <QIcon name="schedule" size="16px" class="q-ml-sm text-grey-7" />
                                            <span class="q-ml-xs text-duration">
                                                {{ getDurationFormattedData(customSLA && customSLA[customSLAPct.origIndex]) }}
                                                <QTooltip
                                                    v-if="customSLA && customSLA[customSLAPct.origIndex]"
                                                    class="bg-gray-bg text-primary"
                                                >
                                                    {{ $t('visualization.SLAInfo.targetDuration') }}:
                                                    {{ customSLA[customSLAPct.origIndex].value }} {{ customSLA[customSLAPct.origIndex].unit }}
                                                </QTooltip>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12 SLAMetricsCell">
                                    <div
                                        v-if="checkActivities(customSLAPct)"
                                        class="row items-center q-gutter-xs SLAMetricsWrap"
                                    >
                                        <div class="SLAStat">
                                            <span class="text-red-11 text-weight-bold">{{ customSLAPct.nonSlaCompliance }}</span>
                                            <QBadge
                                                class="q-ml-xs"
                                                color="red-11"
                                                textColor="white"
                                                :label="`${formatPct(customSLAPct.nonSlaCompliancePct)}%`"
                                            >
                                                <QTooltip class="bg-gray-bg text-primary">
                                                    {{ `${$t('visualization.SLAInfo.nonCompliantCases')}: ${customSLAPct.nonSlaCompliance} (${formatPct(customSLAPct.nonSlaCompliancePct)}%)` }}
                                                </QTooltip>
                                            </QBadge>
                                        </div>

                                        <div class="SLAStat">
                                            <span class="text-green-12 text-weight-bold">{{ customSLAPct.slaCompliance }}</span>
                                            <QBadge
                                                class="q-ml-xs"
                                                color="green-12"
                                                textColor="white"
                                                :label="`${formatPct(customSLAPct.slaCompliancePct)}%`"
                                            >
                                                <QTooltip class="bg-gray-bg text-primary">
                                                    {{ `${$t('visualization.SLAInfo.compliantCases')}: ${customSLAPct.slaCompliance} (${formatPct(customSLAPct.slaCompliancePct)}%)` }}
                                                </QTooltip>
                                            </QBadge>
                                        </div>
                                    </div>
                                    <div
                                        v-else
                                        class="row items-center justify-center"
                                        style="height: 24px;"
                                    >
                                        <QIcon
                                            name="warning"
                                            color="warning"
                                            size="1.6em"
                                        >
                                            <QTooltip class="bg-gray-bg text-primary">
                                                {{ `${$t('visualization.SLAInfo.noSLAInfo')}` }}
                                            </QTooltip>
                                        </QIcon>
                                    </div>
                                </div>
                            </div>
                        </q-card>
                    </div>
                </div>
            </div>

            <div v-if="!customSLA || filteredPercentages.length === 0" class="text-secondary q-pa-sm">
                {{ $t('visualization.SLAInfo.error') }}
            </div>
        </div>

        <q-inner-loading :showing="Object.keys(info).length === 0 || variantsLoading" />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import 'moment-duration-format'
import { filtersMixin } from '@/mixins'

export default {
    name: 'SLAInfoDocked',
    mixins: [filtersMixin],
    props: {
        variantTab: VueTypes.string,
        info: VueTypes.object.def({}),
        chart: VueTypes.object.def({}),
        variantsLoading: VueTypes.bool.def(false),
    },
    data() {
        return {
            cases: this.info.cases,
            customSLA: this.info.customSLA,
            visible: false,
        }
    },
    computed: {
        filteredPercentages() {
            if (!Array.isArray(this.customSLA) || !this.cases) return []
            const percentages = this.getPercentages(this.cases, this.customSLA)
            const currentVariantIdRaw = this.chart?.variantId ?? null
            const currentVariantName = (typeof currentVariantIdRaw === 'string' && currentVariantIdRaw.startsWith('#'))
                ? currentVariantIdRaw
                : null
            const isSingleVariantView = !!currentVariantIdRaw
                || (this.variantTab === 'INDIVIDUAL' && this.info.variants === 1)
                || this.info.variants === 1
            return percentages.filter((sla) => {
                if (sla.variantId === null || sla.variantId === 'all') return true
                if (sla.variantId !== null && sla.variantId !== 'all' && isSingleVariantView) {
                    // Backend: SLA.variantId can be a human label like '#3'.
                    // UI state: chart.variantId is usually a UUID (or an array of UUIDs).
                    // Only do strict filtering when we know the displayed label.
                    return currentVariantName ? `${sla.variantId}` === `${currentVariantName}` : true
                }
                return false
            })
        },
    },
    watch: {
        info: {
            deep: true,
            handler() {
                this.cases = this.info.cases
                this.customSLA = this.info.customSLA
            },
        },
        filteredPercentages(newVal, oldVal) {
            if (oldVal && oldVal.length > 0 && newVal.length === 0) {
                this.visible = false
            }
        },
    },
    methods: {
        getTargetTypeLabel(attributes) {
            const variantId = attributes?.variantId ?? null
            if (variantId === 'all') return this.$t('visualization.SLAInfo.targetType.case')
            if (variantId !== null && variantId !== undefined && variantId !== '') return this.$t('visualization.SLAInfo.targetType.variant')

            switch (attributes?.type) {
                case 'activity':
                    return this.$t('visualization.SLAInfo.targetType.activity')
                case 'transition':
                    return this.$t('visualization.SLAInfo.targetType.transition')
                case 'activityGap':
                    return this.$t('visualization.SLAInfo.targetType.activityGap')
                default:
                    return this.$t('visualization.SLAInfo.targetType.case')
            }
        },
        formatPct(value) {
            if (typeof value !== 'number') return 0
            return Math.round(value)
        },
        getPercentages(cases, SLAs) {
            if (!Array.isArray(SLAs) || SLAs.length === 0 || typeof cases !== 'number' || cases <= 0) return []
            const res = SLAs.map((sla, index) => ({
                variantId: sla.variantId,
                type: sla.type,
                origIndex: index,
                elements: sla.elements,
                nonSlaCompliance: sla.count,
                slaCompliance: cases - sla.count,
                nonSlaCompliancePct: parseFloat(((sla.count / cases) * 100).toFixed(2)),
                slaCompliancePct: parseFloat((((cases - sla.count) / cases) * 100).toFixed(2)),
            })).filter((sla) => {
                if (sla.variantId === null) return true
                if (typeof sla.variantId === 'string') return sla.variantId.length !== 0
                return true
            })
            return res
        },
        getLabel(attributes) {
            const variantId = attributes?.variantId ?? null
            const elements = Array.isArray(attributes?.elements) ? attributes.elements : []

            if (variantId === null) {
                if (elements.length === 1) return elements[0] || ''
                if (elements.length === 2) return elements.filter(Boolean).join(' - ')
                if (elements.length >= 4) {
                    return `${elements[0]} ${elements[1]} - ${elements[2]} ${elements[3]}`
                }
                return elements.filter(Boolean).join(' - ')
            }
            return variantId === 'all' ? this.$t('visualization.SLAInfo.cases') : String(variantId)
        },
        getIcon(attributes) {
            const variantId = attributes?.variantId ?? null
            const elements = Array.isArray(attributes?.elements) ? attributes.elements : []

            if (variantId === null) {
                switch (attributes?.type) {
                    case ('activity'):
                        return 'ACTIVITY'
                    case ('transition'):
                        return 'ARC'
                    case ('activityGap'):
                        return elements.length === 4 ? 'ACTIVITY_GAP' : 'ACTIVITY_GAP'
                    default:
                        return 'ARC'
                }
            }
            return 'VARIANT'
        },
        checkActivities(sla) {
            const elements = Array.isArray(sla?.elements) ? sla.elements : []
            const activityNames = Array.isArray(this.chart?.activities)
                ? this.chart.activities.map((act) => act?.name).filter(Boolean)
                : []
            const arcNames = Array.isArray(this.chart?.arcs)
                ? this.chart.arcs.map((arc) => arc?.name).filter(Boolean)
                : []

            if (sla?.type === 'activity') {
                if (activityNames.length === 0 || elements.length < 1) return false
                return activityNames.includes(elements[0])
            }
            if (sla?.type === 'transition') {
                if (arcNames.length === 0 || elements.length < 1) return false
                return arcNames.includes(elements[0])
            }
            if (sla?.type === 'activityGap' && elements.length === 2) {
                if (activityNames.length === 0) return false
                const firAct = activityNames.includes(elements[0])
                const secAct = activityNames.includes(elements[1])
                return firAct && secAct
            }
            if (sla?.type === 'activityGap' && elements.length === 4) {
                if (activityNames.length === 0) return false
                const firAct = activityNames.includes(elements[0])
                const secAct = activityNames.includes(elements[2])
                return firAct && secAct
            }
            return true
        },
        getDurationFormattedData(customSLA) {
            return customSLA ? moment.duration(customSLA.value, `${customSLA.unit}`).format('M[M] d[d] h[h]:m[m]', { largest: 2, trim: false }) : '-'
        },
    },
}
</script>

<style scoped lang="scss">
.text-style {
    color: $primary;
}

.textPct {
    display: flex;
    color: #000000;
    font-size: x-small;
    padding-top: 8px;
    justify-content: center;
    align-items: center;
}

.text-duration {
    color: $gray-01;
    font-size: 0.85rem;
    font-weight: 600;
}

.SLAMetricsWrap {
    flex-wrap: wrap;
    justify-content: flex-start;
}

.SLAStat {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 4px;
    background: $gray-06;
}


.SLACard {
    border: 1px solid $gray-05;
    border-radius: 6px;
}

.SLACardRow {
    flex-wrap: wrap;
}

.SLAIsCases {
    border-width: 2px;
    border-color: $primary;
}

.SLAInfoCell {
    min-width: 0;
}

.border-red {
    outline: 2px solid red;
    outline-offset: -2px;
}

.border-green {
    outline: 2px solid limegreen;
    outline-offset: -2px;
}
</style>
